import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Stack,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, DeleteSweep as DeleteSweepIcon, Search as SearchIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, VisibilityOutlined as VisibilityOutlinedIcon, WhatsApp as WhatsAppIcon, Phone as PhoneIcon, Email as EmailIcon } from '@mui/icons-material';
import instance from '../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/es';
import LoadingScreen from '../components/LoadingScreen';
import ProductStats from '../components/ProductStats';
import { CATEGORY_HIERARCHY } from '../constants/categories';
import DarkBackground from '../components/DarkBackground';
import { auth } from '../utils/auth';
import LdPlaceHolder from '../assets/ldplaceholder.png';
const PriceDisplay = ({ basePrice, priceBreaks, hidePrice }) => {
  if (hidePrice) {
    return <Typography variant="subtitle1" color="text.secondary">Consultar precio</Typography>;
  }

  const lowestPriceBreak = priceBreaks?.length > 0 ? priceBreaks[priceBreaks.length - 1] : null;
  const displayPrice = lowestPriceBreak && lowestPriceBreak.price < basePrice
    ? { price: lowestPriceBreak.price, quantity: lowestPriceBreak.minQuantity }
    : { price: basePrice, quantity: null };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#424242' }}>
        ${displayPrice.price.toLocaleString()}
      </Typography>
      {priceBreaks?.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          <Divider sx={{ my: 1 }} />

          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mt: 0.5
          }}>
            {priceBreaks
              .slice(0, -1)
              .map((break_, index) => (
                <Typography key={index} variant="caption" color="text.secondary">
                  {break_.minQuantity}+ unidades: ${break_.price.toLocaleString()}
                </Typography>
              ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const MyProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedProductForPause, setSelectedProductForPause] = useState(null);
  const [openPauseDialog, setOpenPauseDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Filter states
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [sortBy, setSortBy] = useState('createdAt-desc');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [filterMenuOpen, setFilterMenuOpen] = useState(!isMobile);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [errorDialog, setErrorDialog] = useState({
    open: false,
    title: '',
    message: ''
  });

  // Auxiliary functions from LandingPage
  const getSubcategories = (category) => {
    if (!category) return [];
    if (CATEGORY_HIERARCHY[category]) {
      return Object.keys(CATEGORY_HIERARCHY[category].subcategories);
    }
    return [];
  };

  const getLeafCategories = (mainCategory, subCategory) => {
    if (!mainCategory || !subCategory) return [];
    return CATEGORY_HIERARCHY[mainCategory]?.subcategories[subCategory] || [];
  };

  const getAllCategories = (mainCategory) => {
    if (!mainCategory) return [];
    const categoryData = CATEGORY_HIERARCHY[mainCategory];
    if (!categoryData || !categoryData.subcategories) return [];

    return getSubcategories(mainCategory).flatMap(sub => {
      // Get leaf categories for this subcategory
      const leafCategories = getLeafCategories(mainCategory, sub);
      // Return both the subcategory and its leaf categories
      return [sub, ...leafCategories];
    });
  };

  // Add this helper function
  const categoryHasProducts = (category, products, mainCategory = null) => {
    return products.some(product => {
      if (mainCategory) {
        // For subcategories and leaf categories
        const allCategories = getAllCategories(mainCategory);
        return allCategories.includes(product.category) && 
               (product.category === category || getLeafCategories(mainCategory, category).includes(product.category));
      }
      // For main categories
      const allCategories = getAllCategories(category);
      return allCategories.includes(product.category);
    });
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Apply filters and sorting
  useEffect(() => {
    let result = [...products];
    
    // Apply category filters
    if (selectedSubCategory) {
      const leafCategories = getLeafCategories(selectedMainCategory, selectedSubCategory);
      if (leafCategories.length > 0) {
        result = result.filter(product => leafCategories.includes(product.category));
      } else {
        result = result.filter(product => product.category === selectedSubCategory);
      }
    } else if (selectedMainCategory) {
      const allCategories = getAllCategories(selectedMainCategory);
      result = result.filter(product => allCategories.includes(product.category));
    }


    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(product => 
        product.name.toLowerCase().includes(search) ||
        product.description.toLowerCase().includes(search) ||
        product.category.toLowerCase().includes(search)
      );
    }
    // Apply sorting
    const [sortField, sortOrder] = sortBy.split('-');
    result.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'createdAt') {
        comparison = new Date(b.createdAt) - new Date(a.createdAt);
      } else if (sortField === 'views') {
        comparison = (b.views || 0) - (a.views || 0);
      }
      return sortOrder === 'desc' ? comparison : -comparison;
    });

    setFilteredProducts(result);
  }, [products, selectedMainCategory, selectedSubCategory, searchTerm, sortBy]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await instance.get('/api/products/my-products');
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await instance.delete(`/api/products/${selectedProduct._id}`);
      setProducts(products.filter(p => p._id !== selectedProduct._id));
      setOpenDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const handlePauseClick = (product, e) => {
    e.stopPropagation();
    setSelectedProductForPause(product);
    setOpenPauseDialog(true);
  };

  const handleToggleStatus = async () => {
    try {
      const response = await instance.patch(`/api/products/${selectedProductForPause._id}/toggle-status`);
      
      // Update the product status in the local state
      setProducts(products.map(p => 
        p._id === selectedProductForPause._id 
          ? { ...p, status: response.data.product.status }
          : p
      ));
      
      setOpenPauseDialog(false);
      setSelectedProductForPause(null);
    } catch (error) {
      setOpenPauseDialog(false);
      setSelectedProductForPause(null);

      // Handle specific error cases
      const errorMessage = error.response?.data?.error;
      if (errorMessage) {
        let dialogTitle = 'Error';
        let dialogMessage = errorMessage;

        // Customize dialog based on error type
        if (errorMessage.includes('suscripción activa')) {
          dialogTitle = 'Suscripción Requerida';
          dialogMessage = 'Necesitas una suscripción activa para hacer visible este producto. ¿Deseas ver nuestros planes?';
        } else if (errorMessage.includes('productos visibles')) {
          dialogTitle = 'Límite de Productos Alcanzado';
          dialogMessage = `${errorMessage}. Considera actualizar tu plan para publicar más productos.`;
        } else if (errorMessage.includes('productos destacados')) {
          dialogTitle = 'Límite de Productos Destacados';
          dialogMessage = `${errorMessage}. Actualiza tu plan para tener más productos destacados.`;
        }

        setErrorDialog({
          open: true,
          title: dialogTitle,
          message: dialogMessage
        });
      } else {
        setErrorDialog({
          open: true,
          title: 'Error',
          message: 'Ha ocurrido un error al cambiar el estado del producto'
        });
      }
    }
  };

  if (loading) return <LoadingScreen message="Cargando tus productos..." />;

  return (
    <DarkBackground>
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: {xs: 1, md: 4}, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{pl:{xs:1},fontSize: {xs: '1.2rem'}}}>
            Mis productos
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/create-product')}
          >
            Nuevo Producto
          </Button>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={3}>
          {/* Filters Section */}
          <Grid item xs={12} md={3}>
            {/* Mobile Filter Header - Only show on mobile */}
            {isMobile && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => setFilterMenuOpen(!filterMenuOpen)}
                >
                  <Typography variant="h6">Filtros</Typography>
                 
                  <IconButton size="small">
                    {filterMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              </Paper>
            )}

            {/* Filter Content - Collapsible on mobile, always shown on desktop */}
            <Collapse in={filterMenuOpen || !isMobile}>
              <Paper sx={{ p: 2, mb: 2 }}>
                
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography variant="h6">Filtros</Typography>
                  
                  <Button
                    size="small"
                    startIcon={<DeleteSweepIcon />}
                    onClick={() => {
                      setSelectedMainCategory('');
                      setSelectedSubCategory('');
                      setSortBy('createdAt-desc');
                      setExpandedCategories([]);
                    }}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                      fontSize: '0.875rem'
                    }}
                  >
                    Limpiar
                  </Button>
                </Box>
                <TextField
                fullWidth
                size="small"
                label="Buscar"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
                              {/* Sort Options */}
                   <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Ordenar por</InputLabel>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      label="Ordenar por"
                    >
                      <MenuItem value="createdAt-desc">Más recientes</MenuItem>
                      <MenuItem value="createdAt-asc">Más antiguos</MenuItem>
                      <MenuItem value="views-desc">Más vistos</MenuItem>
                      <MenuItem value="views-asc">Menos vistos</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Category Filter */}
                <Box >
                  <Typography variant="subtitle2" gutterBottom>Categoría</Typography>
                  <List dense>
                    {Object.entries(CATEGORY_HIERARCHY).map(([key, category]) => (
                      // Only show main category if it has products
                      categoryHasProducts(key, products) && (
                        <React.Fragment key={key}>
                          <ListItem
                            disablePadding
                            sx={{
                              backgroundColor: selectedMainCategory === key ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                            }}
                          >
                            <ListItemButton
                              onClick={() => {
                                if (selectedMainCategory === key) {
                                  setSelectedMainCategory('');
                                  setSelectedSubCategory('');
                                  setExpandedCategories([]);
                                } else {
                                  setSelectedMainCategory(key);
                                  setSelectedSubCategory('');
                                  setExpandedCategories([key]);
                                }
                              }}
                            >
                              <ListItemText
                                primary={category.name}
                                primaryTypographyProps={{
                                  fontSize: '0.9rem',
                                  fontWeight: selectedMainCategory === key ? 'bold' : 'normal'
                                }}
                              />
                            </ListItemButton>
                          </ListItem>

                          {(selectedMainCategory === key || expandedCategories.includes(key)) && (
                            <List component="div" dense disablePadding>
                              {getSubcategories(selectedMainCategory).map((subCategory, index) => (
                                // Only show subcategory if it has products
                                categoryHasProducts(subCategory, products, key) && (
                                  <React.Fragment key={index}>
                                    <ListItem
                                      disablePadding
                                      sx={{ pl: 4,
                                        backgroundColor: (selectedSubCategory === subCategory || expandedCategories.includes(subCategory)) ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                                      }}
                                    >
                                      <ListItemButton
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (selectedSubCategory === subCategory) {
                                            setSelectedSubCategory('');
                                            setExpandedCategories([selectedMainCategory]);
                                          } else {
                                            setSelectedSubCategory(subCategory);
                                            setExpandedCategories([selectedMainCategory, subCategory]);
                                          }
                                        }}
                                      >
                                        <ListItemText
                                          primary={subCategory}
                                          primaryTypographyProps={{
                                            fontSize: '0.85rem',
                                            fontWeight: (selectedSubCategory === subCategory || expandedCategories.includes(subCategory)) ? 'bold' : 'normal',
                                          }}
                                        />
                                      </ListItemButton>
                                    </ListItem>

                                    {(selectedSubCategory === subCategory || expandedCategories.includes(subCategory)) && 
                                     getLeafCategories(selectedMainCategory, subCategory).length > 0 && (
                                      <ListItem>
                                        <List dense sx={{ pl: 2 }}>
                                          {getLeafCategories(selectedMainCategory, subCategory).map((leafCategory, index) => (
                                            <ListItem
                                              key={index}
                                              disablePadding
                                              sx={{ pl: 4,
                                                 backgroundColor: (selectedSubCategory === leafCategory || expandedCategories.includes(leafCategory)) ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                                               }}
                                            >
                                              <ListItemButton
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setSelectedSubCategory(leafCategory);
                                                  setExpandedCategories([selectedMainCategory, subCategory]);
                                                }}
                                              >
                                                <ListItemText
                                                  primary={leafCategory}
                                                  primaryTypographyProps={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: (selectedSubCategory === leafCategory || expandedCategories.includes(leafCategory)) ? 'bold' : 'normal'
                                                  }}
                                                />
                                              </ListItemButton>
                                            </ListItem>
                                          ))}
                                        </List>
                                      </ListItem>
                                    )}
                                  </React.Fragment>
                                )
                              ))}
                            </List>
                          )}
                        </React.Fragment>
                      )
                    ))}
                  </List>
                </Box>

                {/* Bottom expand/collapse button - Only show on mobile */}
                {isMobile && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      width: '100%',
                      pt: 3,
                      cursor: 'pointer'
                    }}
                    onClick={() => setFilterMenuOpen(false)}
                  >
                    <IconButton>
                      <ExpandLessIcon sx={{ fontSize: 35 }} />
                    </IconButton>
                  </Box>
                )}
                
              </Paper>
            </Collapse>
          </Grid>

          {/* Products Grid */}
          <Grid item xs={12} md={9}>
            <Stack spacing={2}>
              {filteredProducts.map((product) => (
                <Card 
                  key={product._id}
                  sx={{
                    display: 'flex',
                    height: {xs: '120px', md: '190px'},
                    transition: 'transform 0.2s ease-in-out',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.01)',
                      boxShadow: 3
                    },
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                  }}
                  onClick={() => navigate(`/product/${product._id}`)}
                >
                  <CardMedia
                    component="img"
                    sx={{ width: '140px', backgroundColor: 'white', height: {xs: 'auto', md:'190px'}, objectFit: 'contain' }}
                    image={product.imageUrls?.[0] ? product.imageUrls?.[0] : LdPlaceHolder}
                    alt={product.name}
                  />
                  <Box sx={{height:'100%', width:'10px', backgroundColor:'#f29b0f'}}> 
                    
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: {xs: 0, md:1 } }}>
                    <CardContent sx={{ flex: '1 0 auto', py: {xs: 0.5, md:1 }, pl: {xs: 0}, pr: {xs: 0.5, md:1 }}}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          
                          <Box display="flex" flexDirection="row" gap={3} alignItems="center">
                          <Typography variant="h6" sx={{
                            fontSize: {xs: '0.8rem', md:'1rem'},
                            m: {xs: 1, md:0},
                            WebkitLineClamp: 2,
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                          >
                            {product.name}
                          </Typography>

                          {!isMobile && <ProductStats product={product} />}  

                              
                        </Box>
                        { !isMobile &&
                            (<Typography variant="body2" color="text.secondary" gutterBottom>
                            Categoría: {CATEGORY_HIERARCHY[product.category]?.name || product.category}
                          </Typography>)
                          }
                          
                        </Box>
                        
                        <Box sx={{display:'flex', flexDirection:'row'}}>
                          <Tooltip title={product.status === 'active' ? "Pausar publicación" : "Activar publicación"}>
                            <IconButton
                              onClick={(e) => handlePauseClick(product, e)}
                              color={product.status === 'active' ? "warning" : "success"}
                            >

                              {((auth.getUserType() == 'subUser' && auth.getUser()?.permissions?.canPauseProducts) || auth.isDistributor() )&& (
                              product.status === 'active' ? <VisibilityOffIcon sx={{fontSize: {xs: '1.2rem'}}}/> : <VisibilityIcon sx={{fontSize: {xs: '1.2rem'}}}  />
                              )}
                            
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Eliminar">
                            <IconButton 
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProduct(product);
                                setOpenDeleteDialog(true);
                              }}
                            >
                              {((auth.getUserType() == 'subUser' && auth.getUser()?.permissions?.canDeleteProducts) || auth.isDistributor() )&& <DeleteIcon sx={{fontSize: {xs: '1.2rem'}}} />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <Box sx={{ mt: 'auto' }}>
                        {!isMobile && <PriceDisplay 
                          basePrice={product.basePrice}
                          priceBreaks={product.priceBreaks}
                          hidePrice={product.hidePrice}
                        />}
                        {isMobile && <ProductStats product={product} />}
                        <Box sx={{ m: {xs: 1, md:0 } }}>
                          {/* {product.availableAmount && <Typography variant="caption" color="text.secondary" display="block">
                            Stock disponible: {product.availableAmount} unidades
                          </Typography>} */}
                          <Typography variant="caption" color="text.secondary">
                            Publicado {moment(product.createdAt).fromNow()}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Box>
                </Card>
              ))}

              {filteredProducts.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No tienes productos publicados
                  </Typography>
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>
          Confirmar Eliminación
        </DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de eliminar el producto {selectedProduct?.name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>
            Cancelar
          </Button>
          <Button onClick={handleDelete} color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openPauseDialog} onClose={() => setOpenPauseDialog(false)}>
        <DialogTitle>
          {selectedProductForPause?.status === 'active' 
            ? 'Pausar Publicación' 
            : 'Activar Publicación'
          }
        </DialogTitle>
        <DialogContent>
          <Typography>
            {selectedProductForPause?.status === 'active'
              ? `¿Está seguro de pausar la publicación de ${selectedProductForPause?.name}? 
                 El producto no será visible para los compradores.`
              : `¿Está seguro de activar la publicación de ${selectedProductForPause?.name}? 
                 El producto será visible para los compradores.`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPauseDialog(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleToggleStatus} 
            color={selectedProductForPause?.status === 'active' ? "warning" : "success"}
            variant="contained"
          >
            {selectedProductForPause?.status === 'active' ? 'Pausar' : 'Activar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={errorDialog.open} 
        onClose={() => setErrorDialog({ ...errorDialog, open: false })}
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          {errorDialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {errorDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          {errorDialog.title === 'Suscripción Requerida' && (
            <Button 
              onClick={() => {
                setErrorDialog({ ...errorDialog, open: false });
                navigate('/pricing');
              }} 
              color="primary"
            >
              Ver Planes
            </Button>
          )}
          {(errorDialog.title === 'Límite de Productos Alcanzado' || 
            errorDialog.title === 'Límite de Productos Destacados' ||
            errorDialog.title === 'Función No Disponible') && (
            <Button 
              onClick={() => {
                setErrorDialog({ ...errorDialog, open: false });
                navigate('/pricing');
              }} 
              color="primary"
            >
              Actualizar Plan
            </Button>
          )}
          <Button 
            onClick={() => setErrorDialog({ ...errorDialog, open: false })} 
            color="primary"
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </DarkBackground>
  );
};

export default MyProducts; 